/**
 * XOP pricing — the company-wide catalogue of what we sell and what it costs
 * (CRM ▸ Pricing, `/crm/pricing`).
 *
 * ONE list, owned by the CEO's price book, is the point: before this, every quote and
 * every invoice re-typed a part number, a description and a unit price by hand,
 * so the "same" line item was priced three different ways across three
 * documents and nothing could be repriced centrally. A price-list ITEM is now
 * the single addressable unit — the CRM's quote/invoice generator picks one and
 * the line fills itself in, and any future surface (agreements, Otto's draft
 * skills, a public price sheet) can read the same rows.
 *
 * SINCE 2026-09-13 A PRICE IS A PRODUCT FEATURE. The book stopped being a
 * second list beside the catalogue: `product_features` carries the part
 * number, the category, the unit and the price, and crm-api's
 * `/api/v1/price_list_items` serves a PROJECTION of those rows so the reseller
 * portal — which quotes from the same prices and cannot call the command
 * center directly — kept reading one unchanged shape. The types below are that
 * shape. Quotes and invoices keep storing
 * their own resolved copy of part number / description / price at the moment
 * they were issued — a price change must never silently rewrite an issued
 * document, so items are COPIED into a line, never referenced from it.
 */

/** Where an item sits in the price book. Mirrors the printed book's sections. */
export const PRICE_LIST_CATEGORIES = [
  "value_added_service",
  "platform_extension",
  "demo_system",
  "support",
  "professional_services",
  "training",
  "hardware",
  "other",
] as const;

export type PriceListCategory = (typeof PRICE_LIST_CATEGORIES)[number];

/**
 * The appliance's own wording (2026-09-15): its licence page heads the two
 * sections "Value Added Services" and "Platform Extensions", and the price book
 * reads the same so a feature is called one thing everywhere.
 */
export const PRICE_LIST_CATEGORY_LABELS: Record<PriceListCategory, string> = {
  value_added_service: "Value Added Services",
  platform_extension: "Platform Extensions",
  demo_system: "Demo System",
  support: "Support & Warranty",
  professional_services: "Professional Services",
  training: "Training",
  hardware: "Hardware",
  other: "Other",
};

/**
 * The TWO sections a licence's features fall into — everywhere in the XOP
 * system, the way the C1 appliance's own licence page shows them (CEO,
 * 2026-09-15): "Value Added Services" first, then "Platform Extensions".
 *
 * The rule is deliberately lopsided. A feature whose price-book category is
 * `value_added_service` is a Value Added Service; EVERY other licensable
 * feature — platform extensions, demo systems, support items, hardware, and
 * the appliance keys nobody sells at all — is a Platform Extension. There is
 * no third heading, and no "other" group: the licence file names appliance
 * keys, and the appliance sorts every one of them into these two.
 *
 * crm-api stamps `section` on every `GET /api/v1/licences/catalog` row with the
 * same rule; `licenceSectionOf` is the client-side twin for a row that predates
 * it. The reseller portal and the support portal carry the same two labels.
 */
export const LICENCE_SECTIONS = ["value_added_services", "platform_extensions"] as const;

export type LicenceSection = (typeof LICENCE_SECTIONS)[number];

export const LICENCE_SECTION_LABELS: Record<LicenceSection, string> = {
  value_added_services: "Value Added Services",
  platform_extensions: "Platform Extensions",
};

export function isLicenceSection(v: unknown): v is LicenceSection {
  return typeof v === "string" && (LICENCE_SECTIONS as readonly string[]).includes(v);
}

/** Which of the two sections a feature of this category belongs to. An unsold
 * key has no category — and is a Platform Extension, like everything that is
 * not a Value Added Service. */
export function licenceSectionOf(category: PriceListCategory | null | undefined): LicenceSection {
  return category === "value_added_service" ? "value_added_services" : "platform_extensions";
}

/** What one unit of the item IS — printed after the price ("$166 / port"). */
export const PRICE_LIST_UNITS = [
  "each",
  "port",
  "session",
  "seat",
  "user",
  "switch",
  "system",
  "month",
  "year",
  "hour",
] as const;

export type PriceListUnit = (typeof PRICE_LIST_UNITS)[number];

export const PRICE_LIST_UNIT_LABELS: Record<PriceListUnit, string> = {
  each: "each",
  port: "per port",
  session: "per session",
  seat: "per seat",
  user: "per user",
  switch: "per switch",
  system: "per system",
  month: "per month",
  year: "per year",
  hour: "per hour",
};

/**
 * How the item is priced. Three modes because the real price book has three:
 * most rows carry a dollar MSRP, the WebRTC package is bundled at no charge,
 * and the Extended Support Package is quoted as 15% of the licences purchased.
 * Flattening those last two into "$0" would have made the list lie.
 */
export const PRICE_LIST_PRICING_MODES = ["fixed", "included", "percent"] as const;

export type PriceListPricingMode = (typeof PRICE_LIST_PRICING_MODES)[number];

export const PRICE_LIST_PRICING_MODE_LABELS: Record<PriceListPricingMode, string> = {
  fixed: "Fixed price",
  included: "Included (no charge)",
  percent: "Percentage of order",
};

/**
 * The money half of a catalogue row — everything the two formatters below
 * need, and nothing else.
 *
 * Structural on purpose: a `PriceListItem` off the projection endpoint and a
 * `ProductFeature` off the catalogue endpoint are the SAME row read through
 * two shapes, so both must be able to print their own price without one of
 * them being converted into the other first.
 */
export interface PricedRow {
  unit: PriceListUnit;
  pricing_mode: PriceListPricingMode;
  price_cents: number;
  percent_rate: number | null;
  currency: string;
}

/** One catalogue row, as served to any client. */
export interface PriceListItem {
  id: number;
  /** XOP part number, e.g. `700-2341-001`. Optional — a few book rows have
   * none — but UNIQUE across the list when present, so it can be the join key
   * for anything that wants to reference an item by its printed identity. */
  part_number: string;
  description: string;
  category: PriceListCategory;
  unit: PriceListUnit;
  pricing_mode: PriceListPricingMode;
  /** Minor units (cents) — integers only, so no float drift accumulates over a
   * 30-line quote. Always 0 unless `pricing_mode === "fixed"`. */
  price_cents: number;
  /** Percentage points (15 = "15% of the order"), `null` off `percent` mode. */
  percent_rate: number | null;
  currency: string;
  notes: string;
  /** Retired items stay in the list (issued documents still name them) but are
   * hidden from every picker. */
  active: boolean;
  /** Manual ordering within a category; ties break on part number. */
  sort_order: number;
  created_at: string;
  updated_at: string;
  updated_by_email: string | null;
}

/**
 * Every optional field spells `| undefined` explicitly. The server parses a
 * write with zod, whose output types optionals as `T | undefined`, and under
 * this repo's `exactOptionalPropertyTypes` a bare `foo?: T` will not accept
 * that — so without this the route would need a cast, which is exactly the
 * place a real shape drift would then hide.
 */
export interface CreatePriceListItemRequest {
  part_number?: string | undefined;
  description: string;
  category?: PriceListCategory | undefined;
  unit?: PriceListUnit | undefined;
  pricing_mode?: PriceListPricingMode | undefined;
  price_cents?: number | undefined;
  percent_rate?: number | null | undefined;
  currency?: string | undefined;
  notes?: string | undefined;
  active?: boolean | undefined;
  sort_order?: number | undefined;
}

/**
 * A patch: every field optional, `undefined` meaning "leave alone".
 * Spelled out rather than `Partial<CreatePriceListItemRequest>` because
 * `Partial` re-emits `description?: string` WITHOUT `| undefined`, which
 * re-opens the exactOptionalPropertyTypes mismatch the create shape avoids.
 */
export interface UpdatePriceListItemRequest {
  part_number?: string | undefined;
  description?: string | undefined;
  category?: PriceListCategory | undefined;
  unit?: PriceListUnit | undefined;
  pricing_mode?: PriceListPricingMode | undefined;
  price_cents?: number | undefined;
  percent_rate?: number | null | undefined;
  currency?: string | undefined;
  notes?: string | undefined;
  active?: boolean | undefined;
  sort_order?: number | undefined;
}

export interface PriceListResponse {
  items: PriceListItem[];
}

export interface PriceListItemResponse {
  item: PriceListItem;
}

/** Error code returned when a part number is already taken. */
export const PRICE_LIST_DUPLICATE_PART_NUMBER = "PRICE_LIST_DUPLICATE_PART_NUMBER";

export function isPriceListCategory(v: unknown): v is PriceListCategory {
  return (
    typeof v === "string" && (PRICE_LIST_CATEGORIES as readonly string[]).includes(v)
  );
}

export function isPriceListUnit(v: unknown): v is PriceListUnit {
  return typeof v === "string" && (PRICE_LIST_UNITS as readonly string[]).includes(v);
}

/**
 * The unit price a document line should START from, in whole currency units.
 *
 * `included` is genuinely free. `percent` seeds at 0 only because the line has
 * not met the rest of the order yet: the document editor re-resolves it off
 * the other lines' subtotal every time they change (`percentOfOrderUnitPrice`
 * below is that rule, and crm-api's `InvoiceTotals` applies the same one on
 * save). An UNPRICED fixed row also seeds at 0 — the operator types the price,
 * and the form refuses to submit a fixed line still at 0.
 */
export function unitPriceFor(item: PricedRow): number {
  return item.pricing_mode === "fixed" ? item.price_cents / 100 : 0;
}

/**
 * What a "15% of order" line charges per unit: the rate applied to the order's
 * PRICED subtotal (every line that is not itself a percentage — fixed, manual
 * and included ones; the last add 0), before any discount or tax, rounded to
 * cents the way crm-api's BigDecimal `.round(2)` rounds. This is the one
 * definition on the client; `InvoiceTotals#resolve_percent_lines!` is its
 * server twin, and the two must agree to the cent or the preview and the
 * saved document differ.
 */
export function percentOfOrderUnitPrice(
  rate: number | null | undefined,
  orderSubtotal: number,
): number {
  const pct = Number(rate);
  const base = Number(orderSubtotal);
  if (!Number.isFinite(pct) || pct <= 0 || !Number.isFinite(base) || base <= 0) return 0;
  // +EPSILON so 4.9995 rounds to 5.00 like BigDecimal, not down through float
  // representation (the same trick the totals calculator uses).
  return Math.round((base * pct) / 100 * 100 + Number.EPSILON) / 100;
}

/**
 * One-line price summary for a picker option: "$166.00 per port", "Included",
 * "15% of order", "No price set". Formatting-only — it prints a price the
 * caller has already decided the viewer may see, and every CRM call site sits
 * behind the financial wall.
 */
export function formatPriceListPrice(item: PricedRow): string {
  if (item.pricing_mode === "included") return "Included";
  if (item.pricing_mode === "percent") {
    return `${item.percent_rate ?? 0}% of order`;
  }
  if (!isPriced(item)) return "No price set";
  const amount = (item.price_cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: item.currency || "USD",
  });
  const unit = PRICE_LIST_UNIT_LABELS[item.unit];
  return item.unit === "each" ? amount : `${amount} ${unit}`;
}

/** Search haystack for an item — part number, description and notes. */
export function priceListSearchText(item: {
  part_number?: string | null;
  description?: string | null;
  notes?: string | null;
}): string {
  return `${item.part_number ?? ""} ${item.description ?? ""} ${item.notes ?? ""}`.toLowerCase();
}

/**
 * Has anybody actually set this price?
 *
 * A brand-new feature is `fixed` at 0 with no part number, which is NOT the
 * same statement as "this costs nothing" — `included` is that statement. The
 * pricing page and the quote picker both say "No price set" for the first and
 * "Included" for the second. The picker still OFFERS an unpriced feature
 * (2026-09-13): it fills the part number and the description and leaves the
 * unit price empty for the operator to type, and the form refuses to submit
 * it until they do — so a `$0.00` line can never be saved by accident.
 */
export function isPriced(item: Pick<PricedRow, "pricing_mode" | "price_cents">): boolean {
  return item.pricing_mode === "fixed" ? item.price_cents > 0 : true;
}
